import React, {useMemo, useState} from 'react';

import {
    ReviewRequestConfig,
    useReviews,
    ReviewFieldSelection,
    UserFieldSelection,
    ReviewResponseFieldSelection,
    ReviewOrdering,
    RatingDistributionItem,
} from '@/lib/hooks/use-reviews';

import {Reviews} from './reviews.react-component';
import type {ReviewsContext} from './types';
import styles from './reviews-container.module.scss';
import {
    TrackedCourse,
    useClpCourse,
} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {ReviewsTabs, ReviewsTitleWithInfo, ReviewTabType} from './reviews-tabs.react-component';
import {useI18n} from '@udemy-v2/i18n';
import {Review} from '@/types/review';
import {CourseRating} from '@udemy-v2/graphql';
// EXPERIMENT: Remove when course variations experiment concludes
import {useCourseRating} from '../../hooks/use-course-rating';

export interface ReviewsContainerProps {
    courseId: number;
    className?: string;
    isAggregatedReviewsEnabled: boolean;
}

export interface ReviewResponseData {
    data: Review[];
    isLoading: boolean;
    ratingDistribution: RatingDistributionItem[];
    averageRating: number;
}

const getUniqueVariantIds = (course: TrackedCourse) => {
    if (!course.variationAggregation) {
        return [];
    }
    const uniqueVariantIds = new Set<string>();
    course.variationAggregation.variations.forEach((variation) => {
        variation?.variants?.forEach((variant) => {
            uniqueVariantIds.add(variant.course.id);
        });
    });
    return Array.from(uniqueVariantIds);
};

export const ReviewsContainer = ({
    courseId,
    className,
    isAggregatedReviewsEnabled,
}: ReviewsContainerProps) => {
    const course = useClpCourse() as TrackedCourse;
    const {gettext} = useI18n();
    const hasWeightedRating =
        isAggregatedReviewsEnabled && !!course.variationAggregation?.variations.length;
    const [activeTab, setActiveTab] = useState<ReviewTabType>(ReviewTabType.ALL_REVIEWS);

    const {isLoading: isRatingLoading, rating} = useCourseRating();

    // Move config creation to useMemo to ensure stable references
    const variationReviewsConfig = useMemo(
        (): ReviewRequestConfig => ({
            courseId: courseId.toString(),
            pageSize: 12,
            isTextReview: true,
            fields: [ReviewFieldSelection.RATING],
            userFields: [UserFieldSelection.IMAGE, UserFieldSelection.DISPLAY_NAME],
            responseFields: [
                ReviewResponseFieldSelection.CONTENT,
                ReviewResponseFieldSelection.USER,
                ReviewResponseFieldSelection.CREATED,
                ReviewResponseFieldSelection.ID,
            ],
            ordering: [ReviewOrdering.CREATED_DESC],
        }),
        [courseId],
    );

    const allReviewsConfig = useMemo((): ReviewRequestConfig => {
        const courseIds = getUniqueVariantIds(course);
        return {
            courseIds,
            pageSize: 12,
            isTextReview: true,
            fields: [ReviewFieldSelection.RATING],
            userFields: [UserFieldSelection.IMAGE, UserFieldSelection.DISPLAY_NAME],
            responseFields: [
                ReviewResponseFieldSelection.CONTENT,
                ReviewResponseFieldSelection.USER,
                ReviewResponseFieldSelection.CREATED,
                ReviewResponseFieldSelection.ID,
            ],
            ordering: [ReviewOrdering.CREATED_DESC],
        };
    }, [course]);

    // All hooks must be called unconditionally at the top level
    const allReviewsData = useReviews(allReviewsConfig);
    const variationReviewsData = useReviews(variationReviewsConfig);

    // EXPERIMENT: Hide reviews container while ratings refetch (reviews data still loads in background)
    if (isRatingLoading) {
        return null;
    }

    const infoTooltipText = gettext(
        'This rating represents a weighted average of learner ratings across courses belonging to this variant group. Individual course ratings may vary.',
    );

    const NoReviewsMessage = (text: string) => (
        <div className={styles['no-reviews-placeholder']}>
            <p className="ud-text-md">{text}</p>
        </div>
    );

    const getVariationRating = (): CourseRating | null | undefined => {
        if (!course.variationAggregation) {
            return undefined;
        }

        for (const variation of course.variationAggregation.variations) {
            for (const variant of variation?.variants ?? []) {
                if (variant?.course?.id === String(courseId)) {
                    return variant.course.rating;
                }
            }
        }
        return undefined;
    };

    const getViewForAggregatedReviews = () => {
        const ratingToUse = activeTab === ReviewTabType.ALL_REVIEWS ? rating : getVariationRating();
        const showInfoTooltip = activeTab === ReviewTabType.ALL_REVIEWS;
        return (
            <>
                <ReviewsTitleWithInfo
                    rating={ratingToUse}
                    showInfoTooltip={showInfoTooltip}
                    infoTooltipText={infoTooltipText}
                />
                <ReviewsTabs
                    allReviewsCount={allReviewsData.count}
                    variationReviewsCount={variationReviewsData.count}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    className={className}
                    renderAllReviews={() => getViewForAllReviews(allReviewsData, allReviewsConfig)}
                    renderVariationReviews={() =>
                        getViewForVariationReviews(
                            variationReviewsData,
                            variationReviewsConfig,
                            true,
                            true,
                        )
                    }
                />
            </>
        );
    };

    const getViewForAllReviews = (
        allReviewsData: ReviewResponseData,
        reviewsConfig: ReviewRequestConfig,
    ) => {
        const {data: reviews, isLoading, ratingDistribution, averageRating} = allReviewsData;

        const reviewsRatings: ReviewsContext = {
            averageRating: course.rating?.average ?? averageRating,
            ratingDistribution,
            courseId,
            page: (reviewsConfig.page ?? 1).toString(),
        };

        if (!isLoading && reviews.length === 0) {
            return NoReviewsMessage(gettext("This course family doesn't have any reviews yet."));
        }

        return (
            <Reviews
                showModalSummaryWidget={true}
                reviewsConfig={reviewsConfig}
                hideReviewTitle={true}
                reviews={reviews}
                isLoading={isLoading}
                reviewsRatings={reviewsRatings}
                reviewableObjectType="course"
                reviewableObjectId={courseId}
                className={className}
                courseId={courseId}
                mobileCarouselClassName={styles['carousel-mobile-full-bleed-grid']}
                tabletCarouselClassName={styles['carousel-tablet-full-bleed-single-item-grid']}
                modalSource="all-reviews"
            />
        );
    };

    const getViewForVariationReviews = (
        reviewsData: ReviewResponseData,
        config: ReviewRequestConfig,
        hideReviewTitle: boolean = false,
        showNoReviewsMessage: boolean = false,
    ) => {
        const {data: reviews, isLoading, ratingDistribution, averageRating} = reviewsData;
        const variationRating =
            (hasWeightedRating ? getVariationRating()?.average : course.rating?.average) ??
            averageRating;

        // By default, use the GraphQL course avg rating, but use the reviews API average rating as a fallback
        const reviewsRatings: ReviewsContext = {
            averageRating: variationRating,
            ratingDistribution,
            courseId,
            page: (config.page ?? 1).toString(),
        };

        if (!isLoading && reviews.length === 0 && showNoReviewsMessage) {
            return NoReviewsMessage(
                gettext(
                    "This variation doesn't have any reviews yet. See what learners say about the full course family.",
                ),
            );
        }

        return (
            <Reviews
                reviewsConfig={config}
                hideReviewTitle={hideReviewTitle}
                reviews={reviews}
                isLoading={isLoading}
                reviewsRatings={reviewsRatings}
                reviewableObjectType="course"
                reviewableObjectId={courseId}
                className={className}
                courseId={courseId}
                mobileCarouselClassName={styles['carousel-mobile-full-bleed-grid']}
                tabletCarouselClassName={styles['carousel-tablet-full-bleed-single-item-grid']}
                modalSource="variation-reviews"
            />
        );
    };

    const getViewAfterFetchForCurrentCourse = () => {
        // Use the already-fetched variationReviewsData from the top level
        return getViewForVariationReviews(
            variationReviewsData,
            variationReviewsConfig,
            false,
            false,
        );
    };

    return hasWeightedRating ? getViewForAggregatedReviews() : getViewAfterFetchForCurrentCourse();
};
